import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';

import { useGetAgencyAssignmentById } from '@nxsys/hooks/api/agency';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Assignment } from '@nxsys/store/slices/assignment/types';
import { GetAgencyAssignmentByIdParams } from '@nxsys/types/api/agency';
import { Icons } from '@nxsys/types/icons';
import { formatStatus } from '@nxsys/utils';
import { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

const AssignmentAgency = ({
  data,
  hasWrapper = true,
  onAddNew,
  onViewDetail,
}: {
  data: IAgency;
  hasWrapper?: boolean;
  onAddNew?: () => void;
  onViewDetail?: (record: Assignment) => void;
}) => {
  const { messages } = useTypedIntl();
  const agencyId = data.id;

  const navigate = useNavigateWithQuery();
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const {
    run: fetchAgencyAssignment,
    loading: isFetchingAgencyAssignment,
    value: agencyAssignmentValue,
  } = useGetAgencyAssignmentById();
  const lastArgs: any = agencyAssignmentValue?.lastArgs ?? {};

  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetAgencyAssignmentByIdParams) => {
    const newParams = { ...params };
    newParams.companyIds = resolveFilterCompanyIds(
      newParams.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    if (agencyId) fetchAgencyAssignment(agencyId, newParams);
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...lastArgs,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [lastArgs],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== lastArgs.size;
    const shouldResetPage = isPageSizeChanged && lastArgs.page > 0;
    handleFetchData({
      ...lastArgs,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...lastArgs,
        page: 0,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...lastArgs,
        page: 0,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleViewDetail = useCallback(
    (record: Assignment) => {
      if (onViewDetail) return onViewDetail(record);
      const id = record?.id;
      if (!id) return;
      navigate(`/assignments/${id}`, {
        query: { from: `/agency/${agencyId}` },
      });
    },
    [agencyId, navigate, onViewDetail],
  );

  const handleGoToCreateAssignment = useCallback(() => {
    navigate('/assignments/create', {
      query: {
        from: `/agency/${agencyId}`,
        defaultValues: JSON.stringify({
          agencyName: { label: data?.name ?? '', value: agencyId },
        }),
      },
    });
  }, [navigate]);

  const handleAddNew = useCallback(() => {
    if (onAddNew) return onAddNew();
    return handleGoToCreateAssignment();
  }, [handleGoToCreateAssignment, onAddNew]);

  const columns: ColumnsType<Assignment> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleViewDetail(record)}>
            <Icons.IconEdit />
          </li>
          {record?.file && (
            <li onClick={() => window.open(record.file, '_blank')}>
              <Icons.IconDownload />
            </li>
          )}
        </ul>
      ),
      width: 120,
    },
    {
      key: 'status',
      title: messages['assignment.columns.status'],
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      fixed: 'left',
      render: (record) => {
        const status = record?.status;
        return (
          <span className={`${formatStatus(status)}`}>
            <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
              {formatStatus(status)}
            </AppTagStatus>
          </span>
        );
      },
      width: 150,
    },
    {
      key: 'assignmentName',
      title: messages['assignment.columns.assignmentName'],
      dataIndex: 'assignmentName',
      width: 250,
      ellipsis: true,
    },
    {
      key: 'subcontractorName',
      title: messages['assignment.columns.employeeSubcontractor'],
      dataIndex: 'subcontractorRef',
      render: (record) => `${record?.forename ?? ''} ${record?.surname ?? ''}`,
      width: 300,
    },
    {
      key: 'startDate',
      title: messages['assignment.columns.startDate'],
      dataIndex: 'startDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) =>
        record && moment(record) ? moment(record)?.format('DD/MM/YYYY') : '_',
    },
    {
      key: 'endDate',
      title: messages['assignment.columns.endDate'],
      dataIndex: 'endDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) =>
        record && moment(record) ? moment(record)?.format('DD/MM/YYYY') : '_',
    },
    {
      key: 'createdBy',
      title: messages['common.createdBy'],
      dataIndex: 'author',
      render: (value) => {
        if (!value) return '-';
        const name = `${value?.forename ?? ''} ${value?.surname ?? ''}`.trim();
        return name || '-';
      },
    },
    {
      key: 'createdOn',
      title: messages['common.createdOn'],
      dataIndex: 'createdAt',
      render: (record) =>
        record && moment(record) ? moment(record)?.format('DD/MM/YYYY') : '_',
    },
  ];

  return (
    <AppCard
      title={hasWrapper ? messages['common.assignment'] : undefined}
      hasBorder={false}
    >
      <AppsHeader>
        <div className="flex justify-between gap-x-2 w-full">
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={handleAddNew}
          />
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={agencyAssignmentValue?.data ?? []}
          loading={isFetchingAgencyAssignment}
          page={lastArgs?.page ?? 0}
          total={agencyAssignmentValue?.totalDocs ?? 0}
          pageSize={lastArgs?.size ?? 20}
          scroll={{ x: 1200, y: 700 }}
          titleEmpty={messages['assignment.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          noDataType={!!searchValue ? 'noDataFound' : 'noDataCreated'}
          addNewBtnOnClick={handleAddNew}
          dataTypeName={messages['common.assignment']}
        />
      </AppsContent>
    </AppCard>
  );
};

export default AssignmentAgency;
